import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { ProductContext } from "../../../store";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";
import LlmsTxtJobsCard from "../../../components/AiVisibility/LlmsTxt/LlmsTxtJobsCard";
import { AI_VISIBILITY_LIST_PAGE_SIZE } from "../../../components/AiVisibility/aiVisibilityListConstants";
import {
	isLlmsTxtJobTerminal,
	listLlmsTxtJobs,
	submitLlmsTxtJob,
} from "../../../services/llmsTxtApi";

const LIST_POLL_MS = 3000;

const AiVisibilityLlmsTxt = () => {
	const navigate = useNavigate();
	const { currentWebsiteId, userLoader, websiteLoader, websiteData } =
		useContext(ProductContext);

	const [jobs, setJobs] = useState([]);
	const [total, setTotal] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [page, setPage] = useState(0);
	const [loadingJobs, setLoadingJobs] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const pollTimerRef = useRef(null);

	const siteUrl = currentWebsiteId
		? websiteData?.[currentWebsiteId]?.websiteURL
		: null;

	useEffect(() => {
		setPage(0);
	}, [currentWebsiteId]);

	const fetchJobs = useCallback(async () => {
		if (!currentWebsiteId) {
			setJobs([]);
			setTotal(0);
			setHasMore(false);
			setLoadingJobs(false);
			return;
		}

		setLoadingJobs(true);
		const result = await listLlmsTxtJobs(currentWebsiteId, {
			skip: page * AI_VISIBILITY_LIST_PAGE_SIZE,
			limit: AI_VISIBILITY_LIST_PAGE_SIZE,
		});

		if (!result.ok) {
			toast.error(result.message);
			setJobs([]);
			setTotal(0);
			setHasMore(false);
		} else {
			setJobs(result.jobs || []);
			setTotal(result.total || 0);
			setHasMore(result.hasMore);
		}
		setLoadingJobs(false);
	}, [currentWebsiteId, page]);

	useEffect(() => {
		fetchJobs();
	}, [fetchJobs]);

	useEffect(() => {
		if (pollTimerRef.current) {
			clearInterval(pollTimerRef.current);
			pollTimerRef.current = null;
		}

		const hasActive = jobs.some((job) => !isLlmsTxtJobTerminal(job?.status));
		if (!hasActive || loadingJobs) return undefined;

		pollTimerRef.current = setInterval(() => {
			fetchJobs();
		}, LIST_POLL_MS);

		return () => {
			if (pollTimerRef.current) {
				clearInterval(pollTimerRef.current);
				pollTimerRef.current = null;
			}
		};
	}, [jobs, loadingJobs, fetchJobs]);

	const handleGenerate = useCallback(async () => {
		if (!currentWebsiteId || submitting) return;

		setSubmitting(true);
		try {
			const result = await submitLlmsTxtJob(currentWebsiteId);
			if (!result.ok || !result.job?.id) {
				toast.error(result.message || "Failed to submit job");
				return;
			}

			setPage(0);
			await fetchJobs();
			navigate(
				`/aivisibility/llms-txt/view?jobId=${encodeURIComponent(result.job.id)}`
			);
		} catch {
			toast.error("Failed to submit job");
		} finally {
			setSubmitting(false);
		}
	}, [currentWebsiteId, submitting, fetchJobs, navigate]);

	if (userLoader || websiteLoader) {
		return <DetailSkeleton />;
	}

	if (!currentWebsiteId) {
		return null;
	}

	return (
		<div className="contentBlock ai-visibility-dashboard llms-txt">
			<div className="llms-txt__page-header mb-2">
				<h2 className="llms-txt__page-title mb-0">LLM Discovery</h2>
				<Button
					color="primary"
					className="llms-txt__generate-btn"
					onClick={handleGenerate}
					disabled={submitting}
				>
					{submitting ? (
						<>
							<Spinner size="sm" className="me-2" />
							Starting…
						</>
					) : (
						"Generate llms.txt"
					)}
				</Button>
			</div>
			<p className="llms-txt__page-subtitle text-muted mb-4">
				Scrape your site, categorize links with AI, and produce a ready-to-deploy llms.txt
				file.
				{siteUrl && (
					<>
						{" "}
						Uses your current website:{" "}
						<a href={siteUrl} target="_blank" rel="noopener noreferrer">
							{siteUrl}
						</a>
					</>
				)}
			</p>

			<LlmsTxtJobsCard
				jobs={jobs}
				loading={loadingJobs}
				total={total}
				page={page}
				pageSize={AI_VISIBILITY_LIST_PAGE_SIZE}
				hasMore={hasMore}
				onPageChange={setPage}
				onGenerate={handleGenerate}
				generating={submitting}
			/>
		</div>
	);
};

export default AiVisibilityLlmsTxt;
